import React, { useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { createTruncatedTextForTable } from '../../../../utils/textUtils';
import ChassisWithExternalDropdown from '../../../../components/common/ChassisWithExternalDropdown';

export default function ServiceBulletinDetailTable({
    items,
    totalCount,
    loading,
    lazyParams,
    onPage,
    sortField,
    sortOrder,
    onSortChange,
    activeFilters,
    onFiltersChange,
    onResetFilters
}) {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);

    const formatCurrency = (value) => {
        if (value == null) return '-';
        return Number(value).toLocaleString('tr-TR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const rows = Array.isArray(items)
        ? items.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` }))
        : [];

    const handleSort = (e) => {
        onSortChange?.(e);
    };

    const tableFilters = useMemo(
        () => ({
            chassisNo: {
                value: activeFilters?.chassisNo ?? null,
                matchMode: FilterMatchMode.EQUALS 
            },
            partNo: {
                value: activeFilters?.partNo ?? null,
                matchMode: activeFilters?.partNoMatchMode ?? FilterMatchMode.CONTAINS
            },
            partDescription: {
                value: activeFilters?.partDescription ?? null,
                matchMode: activeFilters?.partDescriptionMatchMode ?? FilterMatchMode.CONTAINS
            },
            partType: {
                value: activeFilters?.partType ?? null,
                matchMode: activeFilters?.partTypeMatchMode ?? FilterMatchMode.CONTAINS
            },
            menge: {
                value: activeFilters?.menge ?? null,
                matchMode: activeFilters?.mengeMatchMode ?? FilterMatchMode.CONTAINS
            },
            meins: {
                value: activeFilters?.meins ?? null,
                matchMode: activeFilters?.meinsMatchMode ?? FilterMatchMode.CONTAINS
            },
            kurztext: {
                value: activeFilters?.kurztext ?? null,
                matchMode: activeFilters?.kurztextMatchMode ?? FilterMatchMode.CONTAINS
            },
        }),
        [activeFilters]
    );

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};

        const updatedFilters = {
            ...(activeFilters || {}),
            chassisNo: 'chassisNo' in filters ? filters.chassisNo?.value ?? null : activeFilters?.chassisNo ?? null,
            chassisNoMatchMode: FilterMatchMode.EQUALS,  // Dropdown için her zaman EQUALS
            partNo: 'partNo' in filters ? filters.partNo?.value ?? null : activeFilters?.partNo ?? null,
            partNoMatchMode: 'partNo' in filters ? filters.partNo?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.partNoMatchMode ?? FilterMatchMode.CONTAINS,
            partDescription: 'partDescription' in filters ? filters.partDescription?.value ?? null : activeFilters?.partDescription ?? null,
            partDescriptionMatchMode: 'partDescription' in filters ? filters.partDescription?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.partDescriptionMatchMode ?? FilterMatchMode.CONTAINS,
            partType: 'partType' in filters ? filters.partType?.value ?? null : activeFilters?.partType ?? null,
            partTypeMatchMode: 'partType' in filters ? filters.partType?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.partTypeMatchMode ?? FilterMatchMode.CONTAINS,
            menge: 'menge' in filters ? filters.menge?.value ?? null : activeFilters?.menge ?? null,
            mengeMatchMode: 'menge' in filters ? filters.menge?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.mengeMatchMode ?? FilterMatchMode.CONTAINS,
            meins: 'meins' in filters ? filters.meins?.value ?? null : activeFilters?.meins ?? null,
            meinsMatchMode: 'meins' in filters ? filters.meins?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.meinsMatchMode ?? FilterMatchMode.CONTAINS,
            kurztext: 'kurztext' in filters ? filters.kurztext?.value ?? null : activeFilters?.kurztext ?? null,
            kurztextMatchMode: 'kurztext' in filters ? filters.kurztext?.matchMode ?? FilterMatchMode.CONTAINS : activeFilters?.kurztextMatchMode ?? FilterMatchMode.CONTAINS,
        };

        onFiltersChange?.(updatedFilters);
    };

    const actionFilterTemplate = () => {
        return (
            <div className="flex justify-content-center align-items-center">
                <i 
                    onClick={onResetFilters} 
                    className="pi pi-filter-slash cursor-pointer text-blue-500 hover:text-blue-700" 
                    style={{ color: '#0B3187', fontSize: '1rem' }} 
                    title={th('filter.reset')}
                ></i>
            </div>
        );
    };

    // Chassis dropdown filter template
   const chassisFilterTemplate = (options) => {
        return (
           <div style={{ width: '12rem' }}>
            <ChassisWithExternalDropdown
                value={options.value}
                onChange={(chassisId) => options.filterApplyCallback(chassisId)}
                className="p-column-filter"
                showDescription={false}
            />
        </div>
        );
    };

    const createTextColumn = (field, headerKey, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '180px', width, key = field } = options;

        return (
            <Column
                key={key}
                sortable={sortable}
                field={field}
                header={th(headerKey)}
                style={{ minWidth, width: width || minWidth }}
                body={body}
                bodyClassName="text-center"
                filter
                filterField={filterField}
                filterPlaceholder={th('common.search')}
                showFilterMenu={true}
                filterMatchModeOptions={textMatchModeOptions}
                showClearButton={false}
            />
        );
    };

    const createDropdownColumn = (field, headerKey, filterElement, options = {}) => {
        const { filterField = field, body, sortable = true, minWidth = '100px', width, key = field, showFilterMenu = false } = options;

        return (
            <Column 
                key={key} 
                sortable={sortable} 
                field={field} 
                header={th(headerKey)} 
                style={{ minWidth, width: width || minWidth }} 
                body={body} 
                bodyClassName="text-center"
                filter 
                filterField={filterField} 
                filterElement={filterElement} 
                showFilterMenu={showFilterMenu} 
            />
        );
    };

    return (
        <DataTable
            value={rows}
            dataKey="_rowId"
            lazy
            paginator
            rows={lazyParams.rows}
            first={lazyParams.first}
            totalRecords={totalCount}
            onPage={onPage}
            loading={loading}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 25, 50]}
            showGridlines
            tableStyle={{ minWidth: '50rem' }}
            sortField={sortField}
            sortOrder={sortOrder}
            onSort={handleSort}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            ref={dataTableRef}
        >
            <Column
                key="action"

                exportable={false}
                style={{ width: '50px', minWidth: '50px' }}
                sortable={false}
                filter={true}
                filterElement={actionFilterTemplate}
                showFilterMenu={false}
                showClearButton={false}
            />
            {createDropdownColumn('chassisNo', 'serviceBulletinReport.columns.chassisNumber', chassisFilterTemplate, {
                minWidth: '80px',
                width: '80px',
                body: (row) => row?.chassisNo || '-'
            })}
            {createTextColumn('partNo', 'serviceBulletinReport.columns.partNumber', {
                minWidth: '100px',
                width: '100px'
            })}
            {createTextColumn('partDescription', 'serviceBulletinReport.columns.partDescription', {
                minWidth: '200px',
                body: (row) => createTruncatedTextForTable(row?.partDescription || '-', 40)
            })}
            {createTextColumn('partType', 'serviceBulletinReport.columns.partType', {
                minWidth: '100px',
                width: '100px',
                body: (row) => row?.partType || '-'
            })}
            {createTextColumn('menge', 'serviceBulletinReport.columns.quantity', {
                minWidth: '100px',
                width: '100px',
                body: (row) => formatCurrency(row?.menge)
            })}
            {createTextColumn('meins', 'serviceBulletinReport.columns.unit', {
                minWidth: '100px',
                width: '100px'
            })}
        </DataTable>
    );
}